// 'use client';
import React from 'react';

import * as Styled from './styled';

import Link from 'next/link';
import { Simple as Tooltip, TooltipContentProps } from '@/ui/Tooltip';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  theme?: any;
  disabled?: boolean;
  rounded?: boolean;
  stretch?: boolean;
  download?: boolean | string; // download attribute for anchor tags
  small?: boolean;
  smaller?: boolean;
  inline?: boolean;
  tiny?: boolean;
  nowrap?: boolean;
  className?: string;
  testId?: string;
  href?: string;
  target?: string;
  outline?: boolean;
  hideMobile?: boolean;
  tooltip?: React.ReactNode;
  tooltipDisabled?: boolean;
  tooltipSide?: TooltipContentProps['side'];
}

const getButtonSize = (props: ButtonProps): 'small' | 'smaller' | 'tiny' | 'large' | 'inline' => {
  if (props.small) return 'small';
  if (props.smaller) return 'smaller';
  if (props.tiny) return 'tiny';
  if (props.inline) return 'inline';
  return 'large';
};

const mapStyleProps = (props: ButtonProps) => {
  const {
    testId,
    stretch,
    rounded,
    small,
    smaller,
    tiny,
    inline,
    nowrap,
    hideMobile,
    disabled,
    outline,
    className,
    ...rest
  } = props;
  const dataTestId = testId ? `button-${testId}` : undefined;
  const classNames = disabled ? `${className} disabled` : className;

  const size = getButtonSize(props);

  const styledProps = {
    ...rest,
    $stretch: stretch,
    $rounded: rounded,
    $size: size,
    $nowrap: nowrap,
    $hideMobile: hideMobile,
    disabled: disabled,
    className: classNames,
    $outline: outline,
    'data-testid': dataTestId,
  };
  return styledProps;
};

const LinkWrapper: React.FC<{
  href?: string;
  target?: string;
  download?: boolean | string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ children, href, download, target, disabled }) => {
  return href && !disabled ? (
    <Link href={href} passHref target={target} download={download}>
      {children}
    </Link>
  ) : (
    children
  );
};

const TooltipWrapper: React.FC<{
  children: React.ReactNode;
  tooltip: React.ReactNode;
  tooltipSide?: TooltipContentProps['side'];
  tooltipDisabled?: boolean;
}> = ({ children, tooltip, tooltipDisabled, tooltipSide }) => {
  if (tooltipDisabled || !tooltip) {
    return children;
  }
  return (
    <Tooltip content={tooltip} disabled={tooltipDisabled} side={tooltipSide}>
      {children}
    </Tooltip>
  );
};

const ButtonWrapper: React.FC<{
  children: React.ReactNode;
  href?: string;
  target?: string;
  download?: boolean | string;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  tooltipSide?: TooltipContentProps['side'];
  tooltipDisabled?: boolean;
}> = ({ children, href, target, download, disabled, tooltip, tooltipSide, tooltipDisabled }) => {
  return (
    <TooltipWrapper tooltip={tooltip} tooltipDisabled={tooltipDisabled} tooltipSide={tooltipSide}>
      <LinkWrapper href={href} target={target} download={download} disabled={disabled}>
        {children}
      </LinkWrapper>
    </TooltipWrapper>
  );
};

export const Black = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.Button {...styledProps} ref={ref}>
        {children}
      </Styled.Button>
    </ButtonWrapper>
  );
});
Black.displayName = 'Black';

export const Primary = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.PrimaryButton {...styledProps} ref={ref}>
        {children}
      </Styled.PrimaryButton>
    </ButtonWrapper>
  );
});
Primary.displayName = 'Primary';

export const Secondary = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.SecondaryButton {...styledProps} ref={ref}>
        {children}
      </Styled.SecondaryButton>
    </ButtonWrapper>
  );
});
Secondary.displayName = 'Secondary';

export const Basic = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.BasicButton {...styledProps} ref={ref}>
        {children}
      </Styled.BasicButton>
    </ButtonWrapper>
  );
});
Basic.displayName = 'Basic';

export const Success = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.SuccessButton {...styledProps} ref={ref}>
        {children}
      </Styled.SuccessButton>
    </ButtonWrapper>
  );
});
Success.displayName = 'Success';

export const Danger = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.DangerButton {...styledProps} ref={ref}>
        {children}
      </Styled.DangerButton>
    </ButtonWrapper>
  );
});
Danger.displayName = 'Danger';

export const Text = React.forwardRef<HTMLButtonElement, ButtonProps>(({ children, ...props }, ref) => {
  const styledProps = mapStyleProps(props);
  return (
    <ButtonWrapper {...props}>
      <Styled.TextButton {...styledProps} ref={ref}>
        {children}
      </Styled.TextButton>
    </ButtonWrapper>
  );
});
Text.displayName = 'Text';

export const Magic = React.forwardRef<HTMLButtonElement, ButtonProps & { glow?: boolean; outline?: boolean }>(
  ({ children, href, target, glow = true, outline = false, ...props }, ref) => {
    const styledProps = mapStyleProps(props);
    return (
      <ButtonWrapper {...props}>
        {props.disabled ? (
          <>
            {outline ? (
              <Styled.MagicButton {...styledProps} ref={ref}>
                {children}
              </Styled.MagicButton>
            ) : (
              <Styled.PrimaryButton {...styledProps} ref={ref}>
                {children}
              </Styled.PrimaryButton>
            )}
          </>
        ) : (
          <Styled.MagicButtonWrapper $glow={glow} $borderWidth={1}>
            {outline ? (
              <Styled.MagicButton {...styledProps} ref={ref}>
                {children}
              </Styled.MagicButton>
            ) : (
              <Styled.PrimaryButton {...styledProps} ref={ref}>
                {children}
              </Styled.PrimaryButton>
            )}
          </Styled.MagicButtonWrapper>
        )}
      </ButtonWrapper>
    );
  },
);
Magic.displayName = 'Magic';

export default Object.assign(Black, { Primary, Secondary, Black, Basic, Text, Magic, Success, Danger });
